import { motion } from "framer-motion";
import { Check, ArrowRight, HeartPulse } from "lucide-react";

const rcmFeatures = [
  "Eligibility & benefits verification",
  "Prior authorization",
  "Auto coding (AI-assisted)",
  "Charge capture & entry",
  "Claim submission & scrubbing",
  "Payment posting (ERA/835)",
  "Denial management & appeals",
  "AR follow-up",
  "Patient calling & statements",
  "Analytics & reporting",
];

const specialties = [
  "Cardiology",
  "Neurology",
  "Radiology",
  "Orthopedics",
  "Primary Care",
  "Internal Medicine",
  "Oncology",
  "Mental Health",
  "Pediatrics",
];

const billingMetrics = [
  { v: "99.76%", l: "First-Pass Acceptance" },
  { v: "33", l: "Day AR Cycle" },
  { v: "98.5%", l: "Net Collection" },
  { v: "<24h", l: "Charge-to-Claim" },
  { v: "100%", l: "HIPAA Certified" },
  { v: "24 yrs", l: "Experience" },
];

const tiers = [
  {
    name: "Tier 1",
    days: "30 Days",
    practice: "Solo / Small Practice",
    details: "1-3 providers | Single location | Single specialty",
    accent: "bg-[#12B8D0] border-[#12B8D0]",
    steps: [
      ["Week 1", "Discovery & Setup", "BAA, system access, payer list, fee schedule load"],
      ["Week 2", "Configure & Map", "Astra setup, eligibility payer mapping, team assignment"],
      ["Week 3", "Parallel Run", "Shadow billing, KPI baseline, reporting template build"],
      ["Week 4", "Go Live", "Full handoff, dedicated POC assigned, 30-day review"],
    ],
  },
  {
    name: "Tier 2",
    days: "45 Days",
    practice: "Mid-Size Group Practice",
    details: "4-10 providers | 2-3 locations | Multi-specialty",
    accent: "bg-[#F5A623] border-[#F5A623]",
    featured: true,
    steps: [
      ["Week 1-2", "Discovery & Assessment", "Workflow audit, BAA, multi-location access, payer matrix"],
      ["Week 3", "Platform Configuration", "Multi-location setup, fee schedules, specialty coding rules"],
      ["Week 4-5", "Integration & Training", "EHR integration, staff training, reporting templates, KPI baseline"],
      ["Week 6", "Parallel Run & Go Live", "Soft launch, dedicated POC, 30-day post-live review"],
    ],
  },
  {
    name: "Tier 3",
    days: "60 Days",
    practice: "Large / Complex Practice",
    details: "10+ providers | Hospital + Office | Multi-specialty",
    accent: "bg-[#FFE39B] border-[#FFE39B]",
    steps: [
      ["Week 1-2", "Discovery & Strategy", "Full revenue cycle audit, BAA, credentialing review, backlog assessment"],
      ["Week 3-4", "Deep Configuration", "Advanced setup, all locations, hospital and office charge rules"],
      ["Week 5-6", "Integration & Training", "EHR/PMS integration, staff training, payer matrix build, KPI baseline"],
      ["Week 7-8", "Parallel Run & Go Live", "Extended parallel run, guardrails, dedicated POC, 60-day review"],
    ],
  },
];

export const Services = ({ onSelect }) => {
  return (
    <section id="services" data-testid="services" className="relative z-10 bg-white py-16 md:py-24 border-t border-[#D8E6F1]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D8E6F1] bg-[#F2F8FC] px-4 py-1.5 mb-6">
            <HeartPulse size={14} className="text-[#2880B8]" />
            <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#51667A]">Done-for-you RCM</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[1.02]">
            Full-service <span className="font-extrabold text-[#2880B8]">medical billing.</span>
          </h2>
          <p className="mt-6 text-[#51667A] text-lg leading-relaxed">
            Prefer we run the revenue cycle for you? A HIPAA-certified team works every claim end to end - with 24 years of experience and audited results that speak for themselves.
          </p>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-10">
          {rcmFeatures.map((f, i) => (
            <motion.span
              key={f}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="flex items-center gap-2 rounded-full border border-[#D8E6F1] bg-[#F2F8FC] px-4 py-2 text-sm text-[#0C2A40]"
            >
              <Check size={14} className="text-[#2880B8]" /> {f}
            </motion.span>
          ))}
        </div>

        <div className="mb-12">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-4">/ Specialties</p>
          <div className="flex flex-wrap gap-2.5">
            {specialties.map((s) => (
              <span key={s} className="rounded-full border border-[#D8E6F1] bg-white px-4 py-2 text-sm text-[#0C2A40] shadow-[0_12px_30px_-24px_rgba(40,128,184,0.5)]">
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-[#0C2A40] p-8 md:p-10 mb-16">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#9FD0EC] mb-6">/ Audited billing performance</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            {billingMetrics.map((m, i) => (
              <motion.div key={m.l} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}>
                <div className="font-mono text-2xl md:text-3xl font-bold text-white tracking-tight">{m.v}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.12em] uppercase text-[#A9C4DD]">{m.l}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-[#0D2138] p-5 md:p-8">
          <div className="mb-8">
            <h3 className="font-display text-3xl md:text-5xl font-semibold tracking-tight text-white leading-[1.02]">
              Onboarding built around your practice - not a template
            </h3>
            <p className="mt-3 text-[#9FD0EC]">Tiered transition timelines with 2.5% of Practice Collections for every track.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
            {tiers.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                data-testid={`service-tier-${t.name.toLowerCase().replace(" ", "-")}`}
                className={`relative rounded-xl border p-6 flex flex-col ${t.featured ? "border-[#F5A623] bg-[#102A46]" : "border-white/15 bg-[#0F263F]"}`}
              >
                <div className={`rounded-lg ${t.accent} px-5 py-3 text-center font-mono text-[12px] tracking-[0.24em] uppercase ${t.name === "Tier 3" ? "text-[#0C2A40]" : "text-[#071F33]"}`}>
                  {t.name} - {t.days}
                </div>
                <div className="mt-5 text-center">
                  <h4 className={`font-display text-2xl font-bold tracking-tight ${t.name === "Tier 2" ? "text-[#F5A623]" : t.name === "Tier 3" ? "text-[#FFE39B]" : "text-[#12B8D0]"}`}>{t.practice}</h4>
                  <p className="mt-3 text-xs text-[#A9C4DD]">{t.details}</p>
                  <p className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-white">2.5% of Practice Collections</p>
                </div>

                <div className="mt-6 border-t border-white/15 pt-6 space-y-5 flex-1">
                  {t.steps.map(([week, title, body]) => (
                    <div key={week} className="relative pl-8">
                      <span className={`absolute left-0 top-1 h-4 w-4 rounded-full ${t.accent}`} />
                      <p className={`font-mono text-[10px] font-bold ${t.name === "Tier 2" ? "text-[#F5A623]" : t.name === "Tier 3" ? "text-[#FFE39B]" : "text-[#12B8D0]"}`}>{week}</p>
                      <p className="mt-1 font-display text-lg font-semibold text-white">{title}</p>
                      <p className="mt-2 text-sm leading-relaxed text-[#A9C4DD]">{body}</p>
                    </div>
                  ))}
                </div>

                <button
                  data-testid={`service-cta-${t.name.toLowerCase().replace(" ", "-")}`}
                  onClick={() => onSelect?.(`${t.name} billing service`)}
                  className="mt-8 w-full py-4 rounded-full font-mono text-[11px] tracking-[0.15em] uppercase border border-white/20 text-white hover:border-[#9FD0EC] hover:text-[#9FD0EC] transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  Talk to Billing Team <ArrowRight size={14} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
